import {Box, Field, P, Text} from 'exsportia-components'
import {useFormikContext} from 'formik'
import React, {FC, useCallback, useEffect, useMemo} from 'react'

import {getPromoErrorFromResponse} from '../../../api/promo-codes/helpers'
import {PromoError} from '../../../api/promo-codes/types'
import {useSubscription} from '../../../api/subscriptions/hooks'
import {getPassPromoPricing} from '../../../api/subscriptions/promo'
import {SubscriptionPriceTypesEnum} from '../../../api/subscriptions/types'
import {useVenue} from '../../../api/venue/hooks'
import {PricingDetails, PricingElementType} from '../../../components/pricing-details/pricing-details'
import {PromoCodeField} from '../../../components/promo-code-field/promo-code-field'
import {usePromoText} from '../../../components/promo-code-field/use-promo-text'
import {SubscriptionPreviewCard} from '../../../components/subscription-preview-card/subscription-preview-card'
import {safeRound} from '../../../helpers/logic'
import {TranslatorScope} from '../../../locale/types'
import {SubscriptionFormType} from '../../../services/subscription/forms/subscription-purchase-form'
import {TitledSection} from '../../../ui/components/titeled-section/titled-section'
import {useLocale, useLuxon} from '../../../utils/i18n'
import {CycleOption} from '../helpers'
import {InstallmentsOptions} from './installment-options'
import {PaymentOptionsListSelect} from './payment-options-list-select'

type Props = {
  cyclesConfig?: CycleOption | null
}

const PERIOD_KEYS: Partial<Record<SubscriptionPriceTypesEnum, TranslatorScope>> = {
  [SubscriptionPriceTypesEnum.WEEKLY]: 'promo_then_week',
  [SubscriptionPriceTypesEnum.MONTHLY]: 'promo_then_month',
  [SubscriptionPriceTypesEnum.YEARLY]: 'promo_then_year',
}

const setPromoCodeInUrl = (code: string | null) => {
  try {
    const url = new URL(window.location.href)
    if (code) {
      url.searchParams.set('promoCode', code)
    } else {
      url.searchParams.delete('promoCode')
    }
    window.history.replaceState({}, '', url.toString())
  } catch (e) {
    console.log(String(e))
  }
}

export const SubscriptionSummarySection: FC<Props> = ({cyclesConfig}) => {
  const {l} = useLocale()
  const luxon = useLuxon()
  const [startDate, setStartDate] = React.useState<number>(luxon.now().toMillis())
  const {setValues, setFieldValue, values} = useFormikContext<SubscriptionFormType>()
  const {
    data: subscription,
    isFetching: isSubscriptionLoading,
    error: subscriptionError,
  } = useSubscription(
    undefined,
    {promoCode: values.promoCode || undefined, firstCyclePrice: values.promoFirstCyclePrice ?? undefined},
  )
  const {data: venue, isFetching: isVenueLoading} = useVenue({})
  const {discountValue, money} = usePromoText(venue?.currency)

  const setPlan = useCallback((plan: SubscriptionFormType['plan']) => {
    setFieldValue('plan', plan).catch(e => console.log(String(e)))
  }, [values])

  const rejectPromo = useCallback((rejection: PromoError) => {
    void setFieldValue('promoRejection', rejection)
    void setFieldValue('promoCode', null)
    setPromoCodeInUrl(null)
  }, [setFieldValue])

  // The pass endpoint answers a bad code with a 4xx carrying a PROMO_* code (contract §3.3).
  useEffect(() => {
    if (!values.promoCode || isSubscriptionLoading) {
      return
    }
    if (subscriptionError) {
      rejectPromo(getPromoErrorFromResponse(subscriptionError) || {code: 'FALLBACK'})
      return
    }
    if (subscription && !subscription.promoApplied) {
      rejectPromo({code: 'PROMO_NOT_APPLICABLE'})
    }
  }, [subscription, subscriptionError, isSubscriptionLoading, values.promoCode])

  const onApplyPromo = useCallback((code: string) => {
    void setFieldValue('promoRejection', null)
    void setFieldValue('promoCode', code)
    setPromoCodeInUrl(code)
  }, [setFieldValue])

  const onRemovePromo = useCallback(() => {
    void setFieldValue('promoRejection', null)
    void setFieldValue('promoCode', null)
    setPromoCodeInUrl(null)
  }, [setFieldValue])

  const promoApplied = useMemo(() => {
    const applied = subscription?.promoApplied
    if (!applied || !values.promoCode || applied.code.toUpperCase() !== values.promoCode.toUpperCase()) {
      return null
    }
    return applied
  }, [subscription?.promoApplied, values.promoCode])
  const isFirstPaymentOnly = promoApplied?.subscriptionDuration === 'FIRST_PAYMENT'

  const promoPricing = useMemo(
    () =>
      cyclesConfig && promoApplied
        ? getPassPromoPricing({
            cycles: cyclesConfig.cycles,
            plan: cyclesConfig.plan,
            promo: promoApplied,
            listTotal: cyclesConfig.totalPrice,
          })
        : null,
    [cyclesConfig, promoApplied],
  )
  const promoAmount = promoPricing?.discount ?? 0

  const pricingLines = useMemo(() => {
    const plan = values.plan
    if (!subscription || !plan || !values.totalPrice) {
      return null
    }
    const lines: PricingElementType[] = [
      {
        count: 0,
        // With a code: what is due now at list (maybe a part period), so lines − promo = total.
        price: promoApplied && cyclesConfig
          ? safeRound(cyclesConfig.totalPrice - (plan.additionalFee || 0))
          : plan.price,
        text: isFirstPaymentOnly ? l('promo_first_payment') : l('subscription', {caseAction: 'titleCase'}),
      },
    ]
    if (plan.additionalFee) {
      lines.push({
        count: 0,
        price: plan.additionalFee,
        text: l('registrationFee'),
        subtext: l('fee'),
      })
    }
    return lines
  }, [values, promoApplied, isFirstPaymentOnly, cyclesConfig])

  // "15% off your first payment. Then ฿4,500 a month." / "15% off every payment."
  const promoTerms = useMemo(() => {
    const plan = values.plan
    const periodKey = plan ? PERIOD_KEYS[plan.type] : undefined
    if (!promoApplied || !plan || !periodKey) {
      return null
    }
    const discount = discountValue(promoApplied)
    if (!isFirstPaymentOnly) {
      return {summary: l('promo_every_payment_off', {discount}), next: null}
    }
    const summary = `${l('promo_first_payment_off', {discount})} ${l(periodKey, {price: money(plan.price)})}`
    const cycles = cyclesConfig?.cycles || []
    const firstPaidIndex = cycles.findIndex((cycle) => cycle.toPay && cycle.price > 0)
    const nextCycle = firstPaidIndex === -1 ? undefined : cycles[firstPaidIndex + 1]
    const next = nextCycle
      ? l('promo_next_payment', {
          price: money(nextCycle.price),
          date: luxon.fromMillis(nextCycle.startDate).toUTC().toFormat('d LLL yyyy'),
        })
      : null
    return {summary, next}
  }, [promoApplied, isFirstPaymentOnly, values.plan, cyclesConfig, discountValue, money, l, luxon])

  return (
    <Box>
      <SubscriptionPreviewCard id={subscription?.uid} />
      {subscription?.startDateType === 'USER_SELECTED_DATE' ? (
        <TitledSection title={l('startDate')}>
          <Field
            fieldType='datePicker'
            setFieldValue={(_, value) => {
              const picked = luxon.fromJSDate(value as Date)
              const localStartOfDay = picked.startOf('day').toMillis()
              setStartDate(localStartOfDay)
              setValues({
                ...values,
                selectedStartDate: localStartOfDay,
              })
            }}
            input={{
              id: 'dob',
              name: 'dob',
              settings: {
                calendarSettings: {
                  popperTransition: 'none',
                  width: '100%'
                },
              }
            }}
            {...{
              dateFormat: 'dd/LL/yyyy',
              minDate: luxon.now().toJSDate(),
              externalValue: startDate ? luxon.fromMillis(startDate).toLocal().toFormat('dd/LL/yyyy') : null,
              maxDate: subscription.maxStartDaysAmount && !subscription.maxStartDaysUnlimited
                ? luxon.now().plus({days: (subscription.maxStartDaysAmount || 0) + 1}).toJSDate()
                : luxon.now().plus({years: 5}).toJSDate(),
            }}
          />
        </TitledSection>
      ) : (
        subscription?.startDate && (
          <TitledSection title={l('startDate')}>
            <Text>{luxon.fromFormat(subscription.startDate, 'dd/LL/yyyy').toFormat('LLL dd, yyyy')}</Text>
          </TitledSection>
        )
      )}
      <TitledSection title={l('pricingOptions')}>
        <PaymentOptionsListSelect
          select={setPlan}
          selected={values.plan}
          subscriptionId={subscription?.uid}
          promoCode={values.promoCode || undefined}
          firstCyclePrice={values.promoFirstCyclePrice ?? undefined}
        />
      </TitledSection>
      <PromoCodeField
        venueId={venue?.uid}
        items={subscription ? [{entityType: 'subscription', entityId: subscription.uid}] : []}
        code={values.promoCode}
        applied={promoApplied}
        rejection={values.promoRejection}
        isQuoting={isSubscriptionLoading}
        currency={venue?.currency}
        hidden={values.totalPrice === 0}
        onApply={onApplyPromo}
        onRemove={onRemovePromo}
      />
      {promoTerms && (
        <Box mb='12px'>
          <P color='primary'>{promoTerms.summary}</P>
        </Box>
      )}
      {
        (cyclesConfig && Boolean(cyclesConfig.installmentsOptions.length))
        && (
          <TitledSection title={l('installments')}>
            <InstallmentsOptions installmentsOptions={cyclesConfig.installmentsOptions}/>
          </TitledSection>
        )
      }
      {
        Boolean(subscription?.termsAndConditions) && (
          <TitledSection title={l('termsConditions')}>
            <P fontSize={'body'}>{subscription?.termsAndConditions}</P>
          </TitledSection>
        )
      }
      <Box mt={'12px'}>
        <PricingDetails
          discount={0}
          values={pricingLines || []}
          withoutCurrency={false}
          currency={venue?.currency || ''}
          total={values.totalPrice ?? undefined}
          isLoading={isSubscriptionLoading || isVenueLoading || Boolean(promoPricing && !promoPricing.ready)}
          promo={promoApplied && promoAmount > 0 ? {code: promoApplied.code, amount: promoAmount, everyPayment: !isFirstPaymentOnly} : null}
          totalLabel={isFirstPaymentOnly ? l('promo_pay_today') : undefined}
        />
        {promoTerms?.next && (
          <Box mt='8px'>
            <P color='text-secondary'>{promoTerms.next}</P>
          </Box>
        )}
      </Box>
    </Box>
  )
}
